import {
  LinkChip, LinkChipBadges, LinkChipRenderProps, WysiwygProvider,
} from '@cogneed/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useDecisionTreePreviewContext from '../../App/Contexts/DecisionTree/DecisionTreePreviewContext';
import { DecisionTreeRunContext } from '../../App/Contexts/DecisionTree/DecisionTreeRunContext/useDecisionTreeRunContext';
import useDisplay from '../../App/Contexts/GlobalContext/Hooks/useDisplay';
import { DisplayType, useGetCardQuery, useGetDecisionTreeQuery } from '../../generated/core-api/graphql-react-query';
import resolveOpenableUrl from './resolveOpenableUrl';

type CardLinkLabelProps = { cardId: string; tabId?: string; badges: LinkChipBadges } & LinkChipRenderProps;

function CardLinkLabel({
  cardId, tabId, badges, ...chipProps
}: CardLinkLabelProps) {
  const query = useGetCardQuery({ id: cardId });

  if (query.isLoading) {
    // A stable ghost frame, never a raw id or "Loading…" -- and the tab segment's presence is
    // known synchronously (it's stored on the link itself), so a card+tab link ghosts both
    // segments immediately rather than popping the second one in once the title resolves too.
    return (
      <LinkChip
        variant="card"
        title=""
        tab={tabId ? { id: tabId, title: '' } : undefined}
        loading
        badges={badges}
        readOnly={chipProps.readOnly}
        selected={chipProps.selected}
      />
    );
  }

  if ((query.error as Error | null)?.message === 'Unauthorized') {
    // The resolver throws before the card row ever reaches us, so there is no title to show --
    // unlike the other error states, which still have the row (just soft-deleted, or missing
    // the requested tab).
    return <LinkChip variant="card" title="" errorState="unauthorized" badges={badges} {...chipProps} />;
  }

  const card = query.data?.getCard;
  if (!card || card.deletedAt) {
    return <LinkChip variant="card" title={card?.title ?? ''} errorState="target-not-found" badges={badges} {...chipProps} />;
  }
  const tab = tabId ? card.content.tabs.find((t) => t.id === tabId) : undefined;
  if (tabId && !tab) {
    return <LinkChip variant="card" title={card.title} errorState="tab-deleted" badges={badges} {...chipProps} />;
  }
  return <LinkChip variant="card" title={card.title} tab={tab} badges={badges} {...chipProps} />;
}

type DecisionTreeLinkLabelProps = { decisionTreeId: string; badges: LinkChipBadges } & LinkChipRenderProps;

function DecisionTreeLinkLabel({ decisionTreeId, badges, ...chipProps }: DecisionTreeLinkLabelProps) {
  const query = useGetDecisionTreeQuery({ input: { id: decisionTreeId } });

  if (query.isLoading) {
    return <LinkChip variant="tree" title="" loading badges={badges} readOnly={chipProps.readOnly} selected={chipProps.selected} />;
  }

  if ((query.error as Error | null)?.message === 'Unauthorized') {
    return <LinkChip variant="tree" title="" errorState="unauthorized" badges={badges} {...chipProps} />;
  }

  const tree = query.data?.getDecisionTree;
  if (!tree || tree.deletedAt) {
    return <LinkChip variant="tree" title={tree?.name ?? ''} errorState="target-not-found" badges={badges} {...chipProps} />;
  }
  return <LinkChip variant="tree" title={tree.name} badges={badges} {...chipProps} />;
}

// No insert dialogs and no formula: live reads and annotates content rather than authoring
// links, so its toolbar is formatting only -- which is what live's own toolbar did.
export default function LiveWysiwygHost({ children }: { children: React.ReactNode }) {
  // Two namespaces: the link chips' labels are in the default one, the link editor's in
  // wysiwyg. `translated` returns undefined for a key this app does not have, which leaves the
  // design system's own English default in place -- passing t()'s output blindly would render
  // the key path itself, since that is what i18next returns on a miss.
  const { t, i18n } = useTranslation(['wysiwyg', 'translation']);
  const translated = React.useCallback(
    (key: string, ns: string) => (i18n.exists(key, { ns }) ? t(key, { ns }) : undefined),
    [t, i18n],
  );
  const display = useDisplay();
  // Read rather than required: a decision-tree link can appear outside the decision-tree
  // window (in a card, in a compliance example), where no run context exists and there is
  // nothing to start.
  const runContext = React.useContext(DecisionTreeRunContext);
  const isPreview = useDecisionTreePreviewContext();

  const strings = React.useMemo(() => ({
    links: {
      card: translated('decisionTree.tree.editor.card-link', 'translation'),
      decisionTree: translated('decisionTree.tree.editor.decision-tree-link', 'translation'),
      document: translated('decisionTree.tree.editor.document-link', 'translation'),
    },
    linkEditor: {
      edit: translated('editor.link.edit', 'wysiwyg'),
      open: translated('editor.link.open', 'wysiwyg'),
      unlink: translated('editor.link.unlink', 'wysiwyg'),
    },
    // Plain t(), not translated(): this app carries its own copy for these (see
    // public/locales/*/wysiwyg.json), so there is no design-system default to fall back to.
    linkChip: {
      changeTarget: t('editor.linkChip.changeTarget', { ns: 'wysiwyg' }),
      remove: t('editor.linkChip.remove', { ns: 'wysiwyg' }),
      badges: {
        tabDeleted: t('editor.linkChip.badges.tabDeleted', { ns: 'wysiwyg' }),
        targetNotFound: t('editor.linkChip.badges.targetNotFound', { ns: 'wysiwyg' }),
        unauthorized: t('editor.linkChip.badges.unauthorized', { ns: 'wysiwyg' }),
      },
    },
    refresh: translated('refresh', 'wysiwyg'),
  }), [t, translated]);

  const renderCardLink = React.useCallback(
    (cardId: string, tabId: string | undefined, chipProps: LinkChipRenderProps) => (
      <CardLinkLabel cardId={cardId} tabId={tabId} badges={strings.linkChip.badges} {...chipProps} />
    ),
    [strings.linkChip.badges],
  );
  const renderDecisionTreeLink = React.useCallback(
    (decisionTreeId: string, chipProps: LinkChipRenderProps) => (
      <DecisionTreeLinkLabel decisionTreeId={decisionTreeId} badges={strings.linkChip.badges} {...chipProps} />
    ),
    [strings.linkChip.badges],
  );
  const onCardLinkOpen = React.useCallback(
    (cardId: string, tabId?: string) => { display(cardId, DisplayType.CardLink, tabId); },
    [display],
  );
  const onDecisionTreeLinkOpen = React.useCallback(
    (decisionTreeId: string) => { runContext?.start(decisionTreeId, undefined, isPreview); },
    [runContext, isPreview],
  );
  // Live runs inside Electron as well as a browser, so a document or an image opens in a new
  // window rather than replacing the session the agent is in.
  const openInNewWindow = React.useCallback((url: string) => {
    const target = resolveOpenableUrl(url);
    if (!target) {
      console.warn('Ignoring a link with an unusable target', url);
      return;
    }
    window.open(target, '_blank', 'noreferrer');
  }, []);

  return (
    <WysiwygProvider
      locale={i18n.language}
      strings={strings}
      renderCardLink={renderCardLink}
      renderDecisionTreeLink={renderDecisionTreeLink}
      onCardLinkOpen={onCardLinkOpen}
      onDecisionTreeLinkOpen={onDecisionTreeLinkOpen}
      onDocumentLinkOpen={openInNewWindow}
      onImageOpen={openInNewWindow}
      onUrlOpen={openInNewWindow}
    >
      {children}
    </WysiwygProvider>
  );
}
